/**
 * useInvoiceFormState
 *
 * Owns the raw useState/useRef declarations for the invoice form and the
 * reconstructed-address memos derived directly from `initialData`.
 *
 * Returned shape is consumed internally by `useInvoiceForm` and is not part
 * of the public hook API.
 *
 * Grouped state hooks live in ./useInvoiceFormState-hooks.ts; pure helpers
 * (defaults, builders) live in ./useInvoiceFormState-helpers.ts.
 */

import type { Address } from '@/gen/proto'

import {
  useCoreFormState,
  useCustomerPOState,
  useBanksState,
  useWalkinState,
  useCreditLimitState,
  useExportFieldsState,
  useAddressesState,
  useReconstructedAddressMemos,
  useSelectedAddressState,
  useModalState,
  useShippingState,
  useSubmissionState,
} from './useInvoiceFormState-hooks'
import { useBaseCurrency, useEnabledCurrencies } from '../../../hooks/use-currencies'
import { useDateFormat, useSettings } from '../../../stores/settings-store'

import type { LineItem } from './invoice-form-helpers'
import type { AddressSnapshot } from './useInvoiceFormState-types'
import type { Account } from '../../../services/account-service'
import type { BankAccount } from '../../../services/bank-account-service'
import type { Invoice, InvoiceType } from '../../../types/billing'
import type { CustomerPurchaseOrder } from '../../../types/customer-purchase-order'

export type { AddressSnapshot } from './useInvoiceFormState-types'

export interface InvoiceFormStateParams {
  initialData: Partial<Invoice> | undefined
  isVendor: boolean
}

export interface InvoiceFormState {
  // Settings
  dateFormat: string
  baseCurrency: string
  currencyOptions: Array<{ value: string; label: string }>

  // Derived initial flags
  isEditing: boolean
  isEditingPosted: boolean

  // Core form state
  invoiceType: InvoiceType
  setInvoiceType: (t: InvoiceType) => void
  invoiceNumber: string
  setInvoiceNumber: (n: string) => void
  // Supplier's own bill number on a vendor bill. Our internal voucher number
  // (invoiceNumber) is auto-generated on post; this holds the supplier's number.
  supplierBillNumber: string
  setSupplierBillNumber: (n: string) => void
  changeNotes: string
  setChangeNotes: (n: string) => void
  invoiceDate: Date | null
  setInvoiceDate: (d: Date | null) => void
  dueDate: Date | null
  setDueDate: (d: Date | null) => void
  selectedPartyId: string
  setSelectedPartyId: (id: string) => void
  // Party role to post the receivable/payable subledger under, and the
  // active-role options (filtered to this document's direction) to pick from.
  partyRole: string
  setPartyRole: (r: string) => void
  roleOptions: Array<{ value: string; label: string }>
  setRoleOptions: (o: Array<{ value: string; label: string }>) => void
  currency: string
  setCurrency: (c: string) => void
  exchangeRate: string
  setExchangeRate: (r: string) => void
  lineItems: LineItem[]
  setLineItems: React.Dispatch<React.SetStateAction<LineItem[]>>
  notes: string
  setNotes: (n: string) => void
  termsConditions: string
  setTermsConditions: (t: string) => void
  paymentTerms: string
  setPaymentTerms: (t: string) => void
  selectedPaymentTermValue: string
  setSelectedPaymentTermValue: (v: string) => void
  customCreditDays: string
  setCustomCreditDays: (d: string) => void
  poNumber: string
  setPONumber: (n: string) => void
  partyVendorCode: string
  setPartyVendorCode: (c: string) => void
  isRoundOffEnabled: boolean
  setIsRoundOffEnabled: (e: boolean) => void
  placeOfSupply: string
  setPlaceOfSupply: (p: string) => void

  // Customer PO
  customerPOs: CustomerPurchaseOrder[]
  setCustomerPOs: (pos: CustomerPurchaseOrder[]) => void
  isCustomerPOSelectEnabled: boolean
  setIsCustomerPOSelectEnabled: (e: boolean) => void
  selectedCustomerPOId: string
  setSelectedCustomerPOId: (id: string) => void

  // Bank accounts
  bankAccounts: BankAccount[]
  setBankAccounts: (accounts: BankAccount[]) => void
  bankGLAccounts: Account[]
  setBankGLAccounts: (accounts: Account[]) => void
  selectedBankAccountId: string
  setSelectedBankAccountId: (id: string) => void

  // Walk-in / cash mode
  isWalkIn: boolean
  setIsWalkIn: (v: boolean) => void
  cashGLAccounts: Account[]
  setCashGLAccounts: (accounts: Account[]) => void
  selectedCashAccountId: string
  setSelectedCashAccountId: (id: string) => void

  // Credit limit
  partyOutstanding: number
  setPartyOutstanding: (n: number) => void
  creditLimitWarning: string | null
  setCreditLimitWarning: (w: string | null) => void

  // Export-specific
  exportType: 'goods' | 'services'
  setExportType: (t: 'goods' | 'services') => void
  lutNumber: string
  setLutNumber: (n: string) => void
  lutDate: Date | null
  setLutDate: (d: Date | null) => void

  // Address state
  shipToType: 'same' | 'party' | 'custom'
  setShipToType: (t: 'same' | 'party' | 'custom') => void
  customShipToAddress: AddressSnapshot
  consigneeType: 'same' | 'party' | 'custom'
  setConsigneeType: (t: 'same' | 'party' | 'custom') => void
  consigneeName: string
  customConsigneeAddress: AddressSnapshot
  notifyPartyType: 'same' | 'custom' | 'none'
  setNotifyPartyType: (t: 'same' | 'custom' | 'none') => void
  notifyPartyName: string
  customNotifyPartyAddress: AddressSnapshot

  // Selected address objects
  selectedShipToAddress: Address | null
  setSelectedShipToAddress: (a: Address | null) => void
  selectedConsigneeAddress: Address | null
  setSelectedConsigneeAddress: (a: Address | null) => void
  setSelectedNotifyPartyAddress: (a: Address | null) => void

  // Entity addresses
  entityAddresses: Address[]
  setEntityAddresses: (a: Address[]) => void

  // Modal state
  isShipToModalOpen: boolean
  setIsShipToModalOpen: (o: boolean) => void
  isConsigneeModalOpen: boolean
  setIsConsigneeModalOpen: (o: boolean) => void
  isQuickCustomerFormOpen: boolean
  setIsQuickCustomerFormOpen: (o: boolean) => void
  isBillingAddressSelectorOpen: boolean
  setIsBillingAddressSelectorOpen: (o: boolean) => void
  isShippingAddressSelectorOpen: boolean
  setIsShippingAddressSelectorOpen: (o: boolean) => void
  isVersionHistoryVisible: boolean
  setIsVersionHistoryVisible: (v: boolean) => void

  // Export shipping fields
  countryOfDestination: string
  setCountryOfDestination: (c: string) => void
  countryOfDestinationCode: string
  setCountryOfDestinationCode: (c: string) => void
  portOfLoading: string
  setPortOfLoading: (p: string) => void
  portOfDischarge: string
  setPortOfDischarge: (p: string) => void
  shippingBillNumber: string
  setShippingBillNumber: (n: string) => void
  shippingBillDate: Date | null
  setShippingBillDate: (d: Date | null) => void
  portCode: string
  setPortCode: (c: string) => void
  modeOfTransport: 'sea' | 'air' | 'road' | 'rail'
  setModeOfTransport: (m: 'sea' | 'air' | 'road' | 'rail') => void
  vesselFlightNumber: string
  setVesselFlightNumber: (n: string) => void
  blAwbNumber: string
  setBlAwbNumber: (n: string) => void
  containerNumbers: string
  setContainerNumbers: (n: string) => void

  // Submission state
  isSubmitting: boolean
  setIsSubmitting: (s: boolean) => void
  errors: Record<string, string>
  setErrors: React.Dispatch<React.SetStateAction<Record<string, string>>>
  isErrorModalVisible: boolean
  setIsErrorModalVisible: (v: boolean) => void
  isFetchingRate: boolean
  setIsFetchingRate: (f: boolean) => void

  // Exchange rate ref
  currencyChangedByUser: React.MutableRefObject<boolean>

  // Reconstructed address memos
  reconstructedPartyAddress: Address | null
  reconstructedShipToAddress: Address | null
}

export function useInvoiceFormState(params: InvoiceFormStateParams): InvoiceFormState {
  const { initialData, isVendor } = params
  const dateFormat = useDateFormat()
  const { settings } = useSettings()
  const { currencies: currencyOptions } = useEnabledCurrencies()
  const baseCurrency = useBaseCurrency()

  const isEditing = Boolean(initialData?.id)
  const isEditingPosted = !!(initialData?.id && initialData?.status === 'posted')

  const core = useCoreFormState(initialData, baseCurrency, isVendor, settings)
  const customerPO = useCustomerPOState()
  const banks = useBanksState()
  const walkin = useWalkinState()
  const credit = useCreditLimitState()
  const exportFields = useExportFieldsState(initialData)
  const addresses = useAddressesState(initialData)
  const reconstructed = useReconstructedAddressMemos(initialData)
  const selectedAddrs = useSelectedAddressState(initialData, reconstructed)
  const modals = useModalState()
  const shipping = useShippingState(initialData)
  const submission = useSubmissionState()

  return {
    dateFormat, baseCurrency, currencyOptions, isEditing, isEditingPosted,
    ...core, ...customerPO, ...banks, ...walkin, ...credit, ...exportFields,
    ...addresses, ...selectedAddrs, ...modals, ...shipping, ...submission, ...reconstructed,
  }
}
